import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../../tfi/helpers/consts'
import { URL } from '../../../gql/client'
import { Channel } from '../../../types/assignmentGroups'

export const useGroupChannels = (groupId?: string) => {
  const { auth } = useAuth()
  const [channels, setChannels] = useState<Channel[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  const fetchChannels = useCallback(async () => {
    if (!groupId) {
      setChannels([])
      return
    }

    const token = getToken()
    if (!token) {
      setChannels([])
      return
    }

    setLoading(true)
    try {
      const res = await axios.get(`${URL}/api/chat/channels`, {
        params: { scope: 'group', group_id: groupId },
        headers: { Authorization: `Bearer ${token}` },
      })
      setChannels(res.data.items ?? [])
    } catch (error) {
      console.error('Error fetching channels:', error)
      setChannels([])
    } finally {
      setLoading(false)
    }
  }, [groupId, getToken])

  const saveChannel = useCallback(
    async (data: Partial<Channel>) => {
      const token = getToken()
      if (!token || !groupId) return null

      setSaving(true)
      try {
        const payload = { ...data, scope: 'group', group_id: groupId }
        const res = await axios.post(`${URL}/api/chat/channels`, payload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
        await fetchChannels()
        return res.data
      } catch (error) {
        console.error('Error saving channel:', error)
        return null
      } finally {
        setSaving(false)
      }
    },
    [groupId, getToken, fetchChannels]
  )

  const deleteChannel = useCallback(
    async (id: number) => {
      const token = getToken()
      if (!token) return false

      try {
        await axios.delete(`${URL}/api/chat/channels/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        setChannels((prev) => prev.filter((ch) => ch.id !== id))
        return true
      } catch (error) {
        console.error('Error deleting channel:', error)
        return false
      }
    },
    [getToken]
  )

  useEffect(() => {
    fetchChannels()
  }, [fetchChannels])

  return { channels, loading, saving, fetchChannels, saveChannel, deleteChannel }
}
